import type { PagePreview } from "@/pages/tina/festivals/hooks/use-page-preview";

interface PreviewBoxProps {
  title: string;
  preview: PagePreview;
  /** The problems that keep the body from being sent at all. */
  shapeProblems: string[];
  /** What the box says when Tina has nothing to make. */
  emptyText: string;
}

/**
 * What Tina will make in Mood: its own answer, line by line, as it sent it. While the newest edit waits for its answer
 * the older one is dimmed under "Updating…" and never shown as current.
 */
export function PreviewBox({
  title,
  preview,
  shapeProblems,
  emptyText,
}: PreviewBoxProps) {
  const waiting = preview.state === "waiting";
  let message: string | null = null;
  if (preview.state === "blocked") {
    message = shapeProblems.some((p) => p.startsWith("venue_missing"))
      ? "Pick a venue for every day to see what Tina will make."
      : "Fix the problems above to see what Tina will make.";
  } else if (preview.state === "error") {
    message = preview.errorText;
  } else if (preview.lines.length === 0) {
    message =
      preview.problems.length > 0
        ? "Fix the problems above to see what Tina will make."
        : emptyText;
  }

  return (
    <section className="flex flex-col gap-2 rounded-lg border border-primary/40 bg-primary/5 p-4">
      <h2 className="flex items-baseline gap-2 text-base font-semibold">
        {title}
        {waiting ? (
          <span className="text-xs font-normal text-muted-foreground">
            Updating…
          </span>
        ) : null}
      </h2>
      {message !== null ? (
        <p className="text-sm text-muted-foreground">{message}</p>
      ) : (
        <ul className={`list-disc pl-5 text-sm ${waiting ? "opacity-50" : ""}`}>
          {preview.lines.map((line, index) => (
            <li key={`${index}-${line.text}`}>{line.text}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
